import React, { useState } from 'react';
import { X, Star } from 'lucide-react';
import axios from 'axios';
import { useToast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LeaveReviewModal = ({ isOpen, onClose, onSuccess }) => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    suburb: '',
    job_type: '',
    rating: 0,
    review_text: ''
  });
  const [hoverRating, setHoverRating] = useState(0);

  const jobTypes = [
    'Glass Balustrades',
    'Glass Pool Fencing',
    'Pool Fencing Installation',
    'Handrails',
    'Staircases',
    'Glass Installations',
    'Security Fencing',
    'Other'
  ];

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleRatingClick = (rating) => {
    setFormData({ ...formData, rating });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.rating === 0) {
      toast({
        title: "Please select a rating",
        variant: "destructive"
      });
      return;
    }

    if (formData.review_text.length < 10) {
      toast({
        title: "Review too short",
        description: "Please write at least 10 characters",
        variant: "destructive"
      });
      return;
    }

    setIsSubmitting(true);

    try {
      await axios.post(`${BACKEND_URL}/api/reviews`, formData);
      
      toast({
        title: "Thank you for your review!",
        description: "Your recommendation has been submitted successfully."
      });

      // Reset form
      setFormData({
        name: '',
        suburb: '',
        job_type: '',
        rating: 0,
        review_text: ''
      });

      // Call success callback and close modal
      if (onSuccess) onSuccess();
      onClose();
    } catch (error) {
      console.error('Error submitting review:', error);
      toast({
        title: "Submission failed",
        description: "Please try again later.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Leave a Recommendation</h2>
          <button className="modal-close" onClick={onClose} aria-label="Close">
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="review-form">
          <div className="form-group">
            <label htmlFor="name">Your Name *</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              placeholder="John Smith"
            />
          </div>

          <div className="form-group">
            <label htmlFor="suburb">Suburb *</label>
            <input
              type="text"
              id="suburb"
              name="suburb"
              value={formData.suburb}
              onChange={handleChange}
              required
              placeholder="Sydney"
            />
          </div>

          <div className="form-group">
            <label htmlFor="job_type">Job Type *</label>
            <select
              id="job_type"
              name="job_type"
              value={formData.job_type}
              onChange={handleChange}
              required
            >
              <option value="">Select job type</option>
              {jobTypes.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Your Rating *</label>
            <div className="star-rating-input">
              {[1, 2, 3, 4, 5].map((star) => (
                <Star
                  key={star}
                  size={32}
                  className={`star-input ${
                    star <= (hoverRating || formData.rating) ? 'filled' : ''
                  }`}
                  onClick={() => handleRatingClick(star)}
                  onMouseEnter={() => setHoverRating(star)}
                  onMouseLeave={() => setHoverRating(0)}
                  fill={star <= (hoverRating || formData.rating) ? 'currentColor' : 'none'}
                />
              ))}
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="review_text">Your Review * (minimum 10 characters)</label>
            <textarea
              id="review_text"
              name="review_text"
              value={formData.review_text}
              onChange={handleChange}
              required
              rows="5"
              placeholder="Tell us about your experience..."
            />
            <span className="char-count">
              {formData.review_text.length} characters
            </span>
          </div>

          <button
            type="submit"
            className="btn btn-primary btn-submit"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Submitting...' : 'Submit Review'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default LeaveReviewModal;
